import React, { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createCar } from '@/modules/car/actions/async/createCar';
import { prepareUploadCarForm } from '@/modules/car/actions/async/prepare-upload-car-form';
import { useFormOptions } from '@/modules/car/hooks/useFormOptions';
import { CarDocType } from '@/modules/car/model';
import { VehicleCreateForm } from '@/modules/car/model/car-form';
import { getCarForm } from '@/modules/car/store/selectors';
import { carUploadSelectors } from '@/modules/car/store/upload-slice';
import { getCountryCode } from '@/modules/common/store/selectors';
import { AppDispatch } from '@/root-store';
import { eventTracking } from '@/services/event-tracking';
import { useForm } from '@/shared/view/Form';
import { useCollapsedDocsList } from '@/shared/view/Upload/SupplementaryDocs/useCollapsedDocsList';
import { HandleDocListItemClick } from '@src-common/view/components/DocsList';

import { VehicleFormView } from './View';

interface IProps {
  textSubmitButton?: string;
  onOpenUpload: () => void;
  onFinishSubmit: (id: string) => void;
  onSkipSubmit: () => void;
  isSubmitting?: boolean;
  pageNameForEvent: 'to do screen' | 'onboarding';
}

const BaseVehicleForm: React.FC<IProps> = memo(
  ({
    pageNameForEvent,
    onOpenUpload,
    onFinishSubmit,
    onSkipSubmit,
    textSubmitButton,
    isSubmitting,
  }) => {
    useEffect(() => {
      eventTracking.trackSnowplow({
        event: 'Onboarding - Car - Open',
        source: pageNameForEvent,
      });
    }, [pageNameForEvent]);

    const dispatch = useDispatch() as AppDispatch;

    const countryCode = useSelector(getCountryCode);
    const carForm = useSelector(getCarForm);

    const docs = useCollapsedDocsList(useSelector(carUploadSelectors.getDocsList), 'car');

    const handleFormSubmit = useCallback(
      async ({ formValues }: { formValues: VehicleCreateForm }) => {
        if (carForm.id) {
          onSkipSubmit();

          return {};
        }

        const thunkResult = await dispatch(
          createCar({
            formValues,
            onSuccess: (id: string) => {
              eventTracking.track({
                event: 'Onboarding - Car - Success',
                source: pageNameForEvent,
                state: docs.filter(doc => doc.status === 'missing').length === 0,
              });
              onFinishSubmit(id);
            },
          }),
        ).unwrap();

        if (thunkResult.result?.validationErrors) {
          return { validationErrors: thunkResult.result.validationErrors };
        }

        return {};
      },
      [carForm.id, dispatch, onSkipSubmit, pageNameForEvent, docs, onFinishSubmit],
    );

    const { handleSubmit, form } = useForm<VehicleCreateForm>({
      mode: 'onBlur',
      onSubmit: formValues => handleFormSubmit({ formValues }),
      values: carForm,
    });

    const { control, watch, formState, trigger } = form;
    const currentFormValues = watch();

    const formOptions = useFormOptions(currentFormValues.make, currentFormValues.model);

    const handleClickDocListItem = useCallback<HandleDocListItemClick>(
      async (event, doc) => {
        const isFormValid = await trigger();

        if (!isFormValid) {
          return;
        }

        dispatch(
          prepareUploadCarForm({
            docType: doc.docType as CarDocType,
            onSuccess: onOpenUpload,
            vehicleFormValues: currentFormValues,
          }),
        );
      },
      [dispatch, onOpenUpload, trigger, currentFormValues],
    );

    return (
      <VehicleFormView
        isSubmitting={isSubmitting || formState.isSubmitting}
        docs={docs}
        onDocListItemClick={handleClickDocListItem}
        onSubmit={handleSubmit}
        control={control}
        countryCode={countryCode}
        textSubmitButton={textSubmitButton}
        options={formOptions}
      />
    );
  },
);

BaseVehicleForm.displayName = 'VehicleForm';

export { BaseVehicleForm };
